import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useRef } from "react"
import { useTranslation } from "react-i18next"

import {
  TagInput,
  type TagInputHandle,
} from "@/components/candidates/tag-input"
import InputError from "@/components/input-error"
import { OnboardingCountrySelect } from "@/components/onboarding/onboarding-country-select"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { useOnline } from "@/hooks/use-online"
import { candidatesPath } from "@/routes"

interface AddCandidateSheetProps {
  /** Brand display name for the header + brand-chip + a11y description. */
  brandName: string
  /**
   * Sheet open state. Owned by the parent — on the candidate list it is
   * derived from the `?add=1` URL param (`show_add` prop); on the
   * standalone `New` page it is `true` on mount (OKS-629).
   */
  open: boolean
  /**
   * Close request from the Sheet (Escape, outside click, close button,
   * Cancel). The parent decides how to close: the list clears `?add=1`
   * via `closeSheet()`; the standalone page calls `history.back()`.
   * Success does NOT route through here — the server redirect drops
   * `?add=1`, flipping `open` to `false` via the prop.
   */
  onOpenChange: (open: boolean) => void
  /**
   * OKS-657 — deterministic close-focus target for the Base UI popup. On the
   * candidate list the parent passes `finalFocusByTestId("add-candidate-trigger")`
   * so focus lands on the header "Add" control (never `<body>`); the standalone
   * `New` page omits it (no header trigger → Base UI default). Threaded through
   * to `<SheetContent>`, which spreads it onto the popup — no `ui/sheet.tsx` edit.
   */
  finalFocus?: () => HTMLElement | null
  /** Optional initial values (echoed by controller on validation failure). */
  defaultValues?: Partial<{
    tiktok_url: string
    display_name: string
    approx_followers: string
    bio: string
    notes: string
    tags: string
    /** OKS-601 — Optional contact email. */
    email: string
    /** OKS-348 — ISO alpha-2 (e.g. `"FR"`). Default `"FR"` if omitted. */
    country: string
    /** OKS-713 — Freetext city, optional, ≤120 chars. */
    city: string
  }>
}

/**
 * AddCandidateSheet — single-candidate add surface (OKS-629).
 *
 * A side `Sheet` (was a centered `Dialog` pre-OKS-629) whose open state
 * lives in the URL (`?add=1` → `show_add` prop), mirroring the shipped
 * RewardSheet (`Brand/Rewards/Index.tsx`). Open/close survives refresh +
 * share and closes on the server redirect after a successful create.
 *
 * Wraps an Inertia `<Form>` POST to `candidatesPath()` (Backend resolves
 * brand from `Current.brand`, never from URL — brand isolation P0).
 *
 * **Wire shape (locked with be-oks16):** every input's `name` is scoped
 * under `candidate[*]` so Rails reads `params[:candidate][:tiktok_url]`
 * etc. (strong-params nesting). Tags ride a single semicolon-joined
 * string under `candidate[tags]` — see `<TagInput>`.
 *
 * **5 states (UX Spec §States):**
 *   - Loading — submit button → `<Loader2>` + disabled, inputs locked
 *   - Empty — n/a (form *is* the entry point)
 *   - Error — per-field `<InputError>` from the `<Form>` render-prop
 *     `errors`, which are the SOLE error source. Within-brand uniqueness
 *     collision ("already a candidate for this brand") arrives as
 *     `errors.tiktok_url`, NOT `errors.base` (be-oks16 `bea076c`). No
 *     top-level base Alert; the inline `<InputError>` per field is the single
 *     error surface. On a validation failure the controller `redirect_back`s
 *     to the Referer (`/candidates?add=1`), so `show_add` stays `true` and the
 *     Sheet stays open.
 *
 *     OKS-662 — this block used to claim the Sheet + `<Form>` REMOUNT across
 *     that `redirect_back`, leaving the render-prop `errors` empty so that the
 *     page error bag supplied them. **False**, and it is the sentence that
 *     produced OKS-662's ticket, triage, PRD and TRD, all wrong the same way.
 *     There is no remount, but NOT for the reason this comment used to give.
 *     It attributed `preserveState: preserveState ?? _method !== "get"` to
 *     `<Form>`. That expression is real, but it belongs to **`Link`** —
 *     `Form` never mentions `preserveState` at all; it delegates to the
 *     router, and `@inertiajs/core` **`Router#post`** supplies the default:
 *     `visit(url, { preserveState: true, ...options, method: "post", data })`.
 *     A POST therefore resolves `true` and `@inertiajs/react`'s
 *     **`swapComponent`** reuses the component key instead of minting a new
 *     one. Conclusion unchanged — the Form keeps its own errors AND its typed
 *     values across the redirect — only the attribution was wrong.
 *
 *     OKS-920: that wrong attribution was read as governing `Form` by three
 *     people in one afternoon and inverted the mechanism twice. **Cite the
 *     symbol, not the line** — `Router#post` sits at different offsets in the
 *     ESM and CJS builds, so a bare `dist:NNNN` is meaningless without its
 *     artefact and rots on the next bump. The page-error-bag read has been
 *     deleted; it never contributed a visible error, and it rendered STALE
 *     ones on browser Back. See `docs/frontend/inertia-error-bag.md`.
 *   - Success — the controller redirects to the clean list URL (no
 *     `?add`), flipping `open` to `false`; the sonner toast pops from
 *     `flash[:success]`.
 *   - Offline — top `<Alert>` "You appear offline…" + submit disabled
 *     (via `useOnline()`)
 *
 * **In-flight dismissal — intentional swallow-with-feedback** (OKS-667). While
 * a submit is in flight, an Escape / overlay-click dismissal is deliberately
 * BLOCKED (`onOpenChange` returns early via `submittingRef`). The in-flight
 * feedback is the existing "Submitting…" affordance — the Save button is
 * `disabled={processing}` with a spinner. Once the submit resolves the guard is
 * inactive, so a fresh Escape / outside-click closes normally, INCLUDING the
 * validation-error case (the sheet stays open showing inline errors; a new
 * dismissal then closes it).
 *
 * Auto-closing after the submit resolves (defer-and-replay) was implemented and
 * abandoned (CTPO call): on a validation failure the server legitimately
 * re-renders the sheet OPEN with `?add=1` to show the inline errors, and any
 * client-side close — a competing `router.get` OR a raw `history.replaceState`
 * — races that stay-open response non-deterministically (Inertia's async
 * history queue + deferred scroll/restore writes continue past the settled
 * render). Five mechanisms were falsified; swallow-with-feedback is race-free by
 * construction. `submittingRef` mirrors `processing` so the URL-scoped
 * `onOpenChange` reads it without a stale closure.
 *
 * **A11y:** shadcn `<Sheet>` (Base UI Dialog since OKS-615) provides focus
 * trap + Escape + auto-wired `aria-labelledby`/`aria-describedby` (via
 * `SheetTitle` / `SheetDescription`). First field (`tiktok_url`) gets
 * `autoFocus`. Because the Sheet opens via URL nav (`?add=1`) rather than a
 * captured `SheetTrigger`, Base UI has no default return target and focus
 * would otherwise fall to `<body>` on close. OKS-657 pins close-focus with an
 * explicit `finalFocus` getter passed by the parent (see the `finalFocus`
 * prop): on the candidate list, focus deterministically lands on the header
 * "Add" control (`add-candidate-trigger`) on every close path (Escape,
 * outside-click, Cancel, success-redirect). The retargeted Playwright canaries
 * assert that pinned target; the not-trapped check remains a secondary guard.
 *
 * Locked QA testid contract:
 *   - `add-candidate-sheet` (SheetContent) — renamed from
 *     `add-candidate-dialog` at OKS-629
 *   - `add-candidate-form` (Form element)
 *   - `candidate-input-{tiktok-url, display-name, email, approx-followers,
 *      bio, notes, tags, country, city}`
 *   - `candidate-form-error-{field}` per-field error
 *   - `add-candidate-submit` submit button
 *   - `add-candidate-brand-chip`, `add-candidate-offline-banner`
 */
export function AddCandidateSheet({
  brandName,
  open,
  onOpenChange,
  defaultValues,
  finalFocus,
}: AddCandidateSheetProps) {
  const { t } = useTranslation()
  const online = useOnline()

  // Mirror processing into a ref so onOpenChange (lives outside the
  // Form scope) can read the latest value without a stale closure.
  const submittingRef = useRef(false)

  function requestClose() {
    onOpenChange(false)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // OKS-667 — In-flight dismissal is INTENTIONALLY swallowed. While a
        // submit is in flight we block the Escape / outside-click dismissal;
        // the recruiter's in-flight feedback is the existing "Submitting…"
        // affordance (the Save button is `disabled={processing}` + spinner).
        //
        // Auto-closing after the submit resolves (defer-and-replay) was tried
        // and abandoned (CTPO call): on a validation failure the server
        // legitimately re-renders the sheet OPEN with `?add=1` to show the
        // inline errors, and any client-side close (competing `router.get` OR
        // raw `history.replaceState`) races that stay-open response
        // non-deterministically — Inertia's async history queue + deferred
        // scroll/restore writes continue past the settled render (5 mechanisms
        // falsified). Swallowing is race-free by construction.
        //
        // Once the submit resolves the guard is inactive, so a fresh
        // Escape / outside-click closes normally — including the
        // validation-error case (errors shown, then a new dismissal closes).
        // Base UI only fires this for user-driven dismissal, so the
        // server-redirect close path (open → false via prop) never routes here.
        if (!next && submittingRef.current) {
          recordDismissProbe("blocked")
          return
        }
        if (!next) {
          recordDismissProbe("honoured")
          requestClose()
        }
      }}
    >
      <SheetContent
        side="right"
        className="overflow-y-auto sm:max-w-lg"
        data-testid="add-candidate-sheet"
        finalFocus={finalFocus}
      >
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <span>{t("candidates.add.title", { brand: brandName })}</span>
            <Badge
              variant="secondary"
              data-testid="add-candidate-brand-chip"
              data-brand={brandName}
            >
              {brandName}
            </Badge>
          </SheetTitle>
          {/*
           * Helper line under the title — UX Spec §Single-add R1
           * reconciliation. Must remain present verbatim or near-
           * verbatim (UX Lead sign-off requirement).
           */}
          <SheetDescription>{t("candidates.add.description")}</SheetDescription>
        </SheetHeader>

        <Form
          action={candidatesPath()}
          method="post"
          /*
           * OKS-662 — DO NOT ADD `preserveState: false` HERE.
           *
           * `<Form>` does not set `preserveState` itself — it delegates to
           * the router, and `@inertiajs/core` **`Router#post`** supplies the
           * default: `visit(url, { preserveState: true, ...options, ... })`.
           * So this POST gets `true` and `@inertiajs/react`'s
           * **`swapComponent`** reuses the component key. That is the
           * only reason the render-prop `errors` below survive the 422's
           * `redirect_back` and render inline at all. Setting it `false` would
           * remount the Form empty and the inline error would silently vanish —
           * a failed save with nothing on screen explaining it.
           *
           * Until this PR there was a page-error-bag fallback that hid that
           * failure mode. It has been deleted (it caused a worse bug). The
           * thing that now catches this is `candidate-add-flows.spec.ts` E1b,
           * which drives a real 422 and asserts the inline error renders. E1b
           * is load-bearing, not redundant coverage. Do not delete it either.
           */
          options={{ preserveScroll: true }}
          resetOnSuccess
          className="flex flex-col gap-4 px-4 pb-4"
          data-testid="add-candidate-form"
        >
          {({ processing, errors }) => {
            // Sync into the outer ref so onOpenChange sees the latest.
            submittingRef.current = processing
            // OKS-662 — THE INERTIA PAGE ERROR BAG IS NOT A RENDER SOURCE.
            //
            // This used to be `{...usePage().props.errors, ...errors}`. The
            // spread was dead on every path a user can reach with an error they
            // still need: `<Form>` does not remount across the `redirect_back`
            // (`preserveState: true` on POST → same component key), so its own
            // `errors` always shadowed the bag. The bag surfaced in exactly one
            // place — browser Back into `?add=1`, where the history snapshot is
            // restored with `preserveState: false`, the Form DOES remount empty,
            // and the bag rendered the previous attempt's errors on a blank
            // form. Measured, not reasoned: C4 in
            // `tests/playwright/inertia-error-bag-partial-oks662.spec.ts` is
            // red without this deletion.
            //
            // If a future surface genuinely needs to render from the bag, there
            // is a sanctioned way to do it, and it is not this one. Read
            // `docs/frontend/inertia-error-bag.md` first — a manual `only:`
            // partial reload wipes the bag, and the history snapshot keeps a
            // stale copy of it.
            const formErrors = errors as Record<
              string,
              string | string[] | undefined
            >
            return (
              <FormBody
                processing={processing}
                errors={formErrors}
                online={online}
                onCancel={requestClose}
                defaultValues={defaultValues}
                t={t}
              />
            )
          }}
        </Form>
      </SheetContent>
    </Sheet>
  )
}

interface FormBodyProps {
  processing: boolean
  errors: Record<string, string | string[] | undefined>
  online: boolean
  onCancel: () => void
  defaultValues?: AddCandidateSheetProps["defaultValues"]
  t: (key: string, opts?: Record<string, unknown>) => string
}

/**
 * Form fields rendered inside the Inertia `<Form>` render-prop.
 * Split out so the disabled-on-{processing,offline} idiom doesn't
 * recur seven times in a single component scope.
 *
 * Auto-focus the first field on every mount. shadcn Sheet (Base UI
 * Dialog) already sets initial focus to the first focusable element,
 * but we explicitly set `autoFocus` on the URL input so the contract
 * is stable in tests where the Sheet opens via the `open` prop.
 */
function FormBody({
  processing,
  errors,
  online,
  onCancel,
  defaultValues,
  t,
}: FormBodyProps) {
  const tagInputRef = useRef<TagInputHandle | null>(null)

  const disabled = processing || !online

  return (
    <>
      {!online && (
        <Alert
          variant="default"
          data-testid="add-candidate-offline-banner"
          role="status"
        >
          <AlertTitle>{t("candidates.bulk.errors.offline.title")}</AlertTitle>
          <AlertDescription>
            {t("candidates.add.offline_helper")}
          </AlertDescription>
        </Alert>
      )}

      {/*
       * No `errors.base` Alert. Backend (be-oks16 `bea076c`) confirmed
       * the contract never ships `errors[:base]` — within-brand
       * uniqueness collision lands on `errors.tiktok_url` (code
       * `:taken`) so the inline `<InputError>` on the URL input is
       * the single error surface. Dropping the base alert keeps the
       * SR experience focused (one error region per submission).
       *
       * If a future ticket needs a non-field error path (e.g. brand-
       * access revoked mid-flow), revisit by re-introducing a top
       * Alert AND coordinating with backend to actually populate
       * `errors[:base]`.
       */}

      <Field
        id="candidate-tiktok-url"
        name="candidate[tiktok_url]"
        label={t("candidates.add.fields.tiktok_url.label")}
        helper={t("candidates.add.fields.tiktok_url.helper")}
        placeholder={t("candidates.add.fields.tiktok_url.placeholder")}
        required
        autoFocus
        defaultValue={defaultValues?.tiktok_url}
        disabled={disabled}
        error={pickError(errors.tiktok_url)}
        testIdInput="candidate-input-tiktok-url"
        testIdError="candidate-form-error-tiktok-url"
      />

      <Field
        id="candidate-display-name"
        name="candidate[display_name]"
        label={t("candidates.add.fields.display_name.label")}
        placeholder={t("candidates.add.fields.display_name.placeholder")}
        required
        defaultValue={defaultValues?.display_name}
        disabled={disabled}
        error={pickError(errors.display_name)}
        testIdInput="candidate-input-display-name"
        testIdError="candidate-form-error-display-name"
      />

      {/*
       * OKS-601 — Optional contact email, placed directly below Display
       * name (contact grouped with identity, per UX Spec §2). No asterisk
       * (optional); `type="email"` + `inputMode="email"` for keyboard
       * affordance. Server-side `URI::MailTo::EMAIL_REGEXP` + `allow_blank`
       * is authoritative — no HTML5 `required`, so a blank submit reaches
       * the Inertia roundtrip cleanly. Errors arrive on `errors.email`.
       */}
      <Field
        id="candidate-email"
        name="candidate[email]"
        // OKS-601 — `type="text"` + `inputMode="email"` (NOT `type="email"`).
        // Native email constraint validation would block submit on an
        // invalid address and pre-empt the server-authoritative inline
        // error (PRD AC5 — invalid rejected inline on add). Matches the
        // OKS-348 country/city fields' server-first posture; `inputMode`
        // still surfaces the email keyboard on mobile.
        type="text"
        inputMode="email"
        label={t("candidates.add.fields.email.label")}
        helper={t("candidates.add.fields.email.helper")}
        placeholder={t("candidates.add.fields.email.placeholder")}
        defaultValue={defaultValues?.email}
        disabled={disabled}
        error={pickError(errors.email)}
        testIdInput="candidate-input-email"
        testIdError="candidate-form-error-email"
      />

      <Field
        id="candidate-approx-followers"
        name="candidate[approx_followers]"
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        label={t("candidates.add.fields.approx_followers.label")}
        helper={t("candidates.add.fields.approx_followers.helper")}
        placeholder={t("candidates.add.fields.approx_followers.placeholder")}
        defaultValue={defaultValues?.approx_followers}
        disabled={disabled}
        error={pickError(errors.approx_followers)}
        testIdInput="candidate-input-approx-followers"
        testIdError="candidate-form-error-approx-followers"
      />

      <FieldTextarea
        id="candidate-bio"
        name="candidate[bio]"
        label={t("candidates.add.fields.bio.label")}
        placeholder={t("candidates.add.fields.bio.placeholder")}
        defaultValue={defaultValues?.bio}
        disabled={disabled}
        error={pickError(errors.bio)}
        testIdInput="candidate-input-bio"
        testIdError="candidate-form-error-bio"
      />

      <FieldTextarea
        id="candidate-notes"
        name="candidate[notes]"
        label={t("candidates.add.fields.notes.label")}
        placeholder={t("candidates.add.fields.notes.placeholder")}
        defaultValue={defaultValues?.notes}
        disabled={disabled}
        error={pickError(errors.notes)}
        testIdInput="candidate-input-notes"
        testIdError="candidate-form-error-notes"
      />

      <div className="flex flex-col gap-2">
        <Label htmlFor="candidate-tags">
          {t("candidates.add.fields.tags.label")}
        </Label>
        <TagInput
          ref={tagInputRef}
          id="candidate-tags"
          name="candidate[tags]"
          defaultValue={
            defaultValues?.tags ? defaultValues.tags.split(";") : []
          }
          disabled={disabled}
          invalid={pickError(errors.tags) !== null}
          placeholder={t("candidates.add.fields.tags.placeholder")}
          describedBy="candidate-tags-helper candidate-form-error-tags"
          testId="candidate-input-tags"
        />
        <p id="candidate-tags-helper" className="text-muted-foreground text-xs">
          {t("candidates.add.fields.tags.helper")}
        </p>
        <InputError
          id="candidate-form-error-tags"
          data-testid="candidate-form-error-tags"
          messages={pickErrorMessages(errors.tags)}
        />
      </div>

      {/*
       * Country Select (alpha-2) + City Input for candidate location.
       * Wire names scoped under `candidate[country]` / `candidate[city]`
       * so the controller's strong-params pipeline picks them up directly.
       *
       * Layout: stacked <640px, `[2fr][1fr]` grid ≥640px (UX Spec §2-A).
       */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-2">
          {/*
           * OKS-348 — `required` is enforced server-side via
           * `Candidate#country` presence + format + inclusion
           * validators. The HTML5 `required` attribute is intentionally
           * NOT set on the hidden input the Select bridges to — it
           * short-circuits the browser submission before the
           * Inertia roundtrip can produce localized server-side errors
           * (PRD Q5 + UX-Spec §9 #5: "submit-failure shows inline
           * error"). Visual `*` asterisk on the label preserves the
           * required affordance.
           */}
          <Label htmlFor="candidate-country">
            {t("candidates.add.fields.country.label")}
            <span className="text-destructive" aria-hidden="true">
              {" *"}
            </span>
          </Label>
          <OnboardingCountrySelect
            name="candidate[country]"
            id="candidate-country"
            testId="candidate-input-country"
            defaultValue={defaultValues?.country ?? "FR"}
            required={false}
            disabled={disabled}
            errorMessages={pickErrorMessages(errors.country)}
            errorId="candidate-form-error-country"
          />
          <InputError
            id="candidate-form-error-country"
            data-testid="candidate-form-error-country"
            messages={pickErrorMessages(errors.country)}
          />
        </div>

        <div className="flex flex-col gap-2">
          {/*
           * OKS-713 — City is OPTIONAL. No `*` marker and no
           * `aria-required`: the model (`Candidate#city`) no longer
           * validates presence, so a blank City saves. The ≤120 length
           * cap still applies server-side and surfaces via the same
           * localized inline `<InputError>` below when exceeded.
           */}
          <Label htmlFor="candidate-city">
            {t("candidates.add.fields.city.label")}
          </Label>
          <Input
            id="candidate-city"
            name="candidate[city]"
            type="text"
            maxLength={120}
            placeholder={t("candidates.add.fields.city.placeholder")}
            defaultValue={defaultValues?.city ?? ""}
            disabled={disabled}
            aria-invalid={pickError(errors.city) !== null ? "true" : undefined}
            aria-describedby="candidate-form-error-city"
            data-testid="candidate-input-city"
          />
          <InputError
            id="candidate-form-error-city"
            data-testid="candidate-form-error-city"
            messages={pickErrorMessages(errors.city)}
          />
        </div>
      </div>

      {/*
       * Footer — plain flex row (was `DialogFooter` pre-OKS-629). The
       * Sheet scrolls internally via `SheetContent`'s `overflow-y-auto`,
       * so the footer flows at the bottom of the form and never clips on
       * short viewports (UX Spec §Responsive).
       */}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {/*
         * Mobile touch-target bump (UX Lead sign-off requirement +
         * WCAG 2.5.5 / iOS HIG ≥44px). The shadcn Button default is
         * h-9 (36px) which fails the minimum on the < sm viewport;
         * `max-sm:h-11` (44px) brings it up without touching the
         * shared shadcn primitive (`components/ui/button.tsx` — a ledgered
         * edit is permitted with parity/a11y coverage, see
         * `docs/frontend/README.md` rule 6, but a per-call class is cheaper here).
         */}
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
          aria-disabled={processing ? "true" : undefined}
          data-testid="add-candidate-cancel"
          className="max-sm:h-11"
        >
          {t("candidates.add.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={disabled}
          aria-disabled={disabled ? "true" : undefined}
          data-testid="add-candidate-submit"
          className="max-sm:h-11"
        >
          {processing && (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          )}
          {processing
            ? t("candidates.add.submitting")
            : t("candidates.add.submit")}
        </Button>
      </div>
    </>
  )
}

interface FieldProps {
  id: string
  name: string
  label: string
  helper?: string
  placeholder?: string
  required?: boolean
  autoFocus?: boolean
  defaultValue?: string
  disabled?: boolean
  type?: string
  inputMode?: "text" | "numeric" | "email" | "url"
  min?: number
  step?: number
  error: string | null
  testIdInput: string
  testIdError: string
}

function Field({
  id,
  name,
  label,
  helper,
  placeholder,
  required,
  autoFocus,
  defaultValue,
  disabled,
  type = "text",
  inputMode,
  min,
  step,
  error,
  testIdInput,
  testIdError,
}: FieldProps) {
  const helperId = helper ? `${id}-helper` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      <Input
        id={id}
        name={name}
        type={type}
        inputMode={inputMode}
        min={min}
        step={step}
        defaultValue={defaultValue}
        placeholder={placeholder}
        // OKS-661 — HTML5 `required` intentionally dropped so a blank
        // submit reaches the server and renders a localized inline
        // `<InputError>` instead of the browser's untranslated native
        // bubble. Required-ness stays signaled via the visual `*`
        // marker on the label + `aria-required` below. Mirrors the
        // OKS-348 city/country precedent + the email field on this Sheet.
        autoFocus={autoFocus}
        disabled={disabled}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={testIdInput}
      />
      {helper && (
        <p id={helperId} className="text-muted-foreground text-xs">
          {helper}
        </p>
      )}
      <InputError
        id={errorId}
        data-testid={testIdError}
        messages={error ? [error] : undefined}
      />
    </div>
  )
}

interface FieldTextareaProps {
  id: string
  name: string
  label: string
  placeholder?: string
  defaultValue?: string
  disabled?: boolean
  error: string | null
  testIdInput: string
  testIdError: string
}

function FieldTextarea({
  id,
  name,
  label,
  placeholder,
  defaultValue,
  disabled,
  error,
  testIdInput,
  testIdError,
}: FieldTextareaProps) {
  const errorId = error ? `${id}-error` : undefined
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        name={name}
        defaultValue={defaultValue}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={errorId}
        data-testid={testIdInput}
      />
      <InputError
        id={errorId}
        data-testid={testIdError}
        messages={error ? [error] : undefined}
      />
    </div>
  )
}

/**
 * OKS-667 — Test-only diagnostic instrument for the in-flight dismissal
 * (PRD §7). Splits every non-close routed through the Sheet's `onOpenChange`
 * into two disjoint worlds so an investigation can attribute a residual
 * "panel wouldn't close":
 *   - `blocked`  — the in-flight guard fired (dismissal deferred).
 *   - `honoured` — a normal close was honored immediately.
 * A residue where BOTH stay 0 means the keypress never reached the handler
 * at all (focus/harness/environment — a different bug, out of this ticket).
 *
 * The counter object exists ONLY when a Playwright test creates it
 * (`window.__oks667DismissProbe = { blocked: 0, honoured: 0 }`) before
 * driving the sheet, then reads it via `page.evaluate` afterwards. In
 * production the object is never created, so this guard no-ops and no debug
 * state ships. (Note: `probe?.[kind]++` is a SyntaxError — optional chaining
 * cannot sit on the left of `++` — hence the explicit `if (probe)` guard.)
 */
function recordDismissProbe(kind: "blocked" | "honoured"): void {
  if (typeof window === "undefined") return
  const probe = window.__oks667DismissProbe
  if (probe) probe[kind] += 1
}

/**
 * Inertia render-prop `errors` is loosely typed — pull a single
 * string-or-array entry to the canonical "first non-empty string"
 * shape used in the field-level surfaces.
 */
function pickError(input: string | string[] | undefined): string | null {
  if (!input) return null
  if (Array.isArray(input)) {
    const first = input.find((entry) => Boolean(entry))
    return first ?? null
  }
  return input || null
}

function pickErrorMessages(
  input: string | string[] | undefined,
): string[] | undefined {
  if (!input) return undefined
  if (Array.isArray(input)) return input
  return [input]
}
